type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error' | 'silent'

const LOG_LEVELS: { [L in LogLevel]: L } = {
  debug: 'debug',
  log: 'log',
  info: 'info',
  warn: 'warn',
  error: 'error',
  silent: 'silent',
} as const

/**
 * Gets the log level based on the environment.
 */
const getLogLevel = (): LogLevel => {
  switch (process.env.NODE_ENV) {
    /**
     * Vercel sets NODE_ENV to 'production' for all deployments, both production
     * and preview, so we use our custom DEPLOYMENT_TARGET environment variable
     * to differentiate between prod and preview.
     */
    case 'production': {
      switch (process.env.DEPLOYMENT_TARGET) {
        case 'production':
          /**
           * Sets the log level in production. Changing this to anything other
           * than 'silent' will cause the logger to output logs in production,
           * which is not recommended because it can pose security and data
           * privacy risks.
           */
          return LOG_LEVELS.silent
        case 'preview':
          /**
           * Sets the log level in preview environments. Changing this is low
           * risk and can be helpful during testing (but try not to accidentally
           * commit changes to it).
           */
          return LOG_LEVELS.debug
        default:
          return LOG_LEVELS.silent
      }
    }
    case 'development':
      /**
       * Sets the log level in development environments. Changing this is very
       * low risk, and can be helpful during development (but try not to
       * accidentally commit changes to it).
       */
      return LOG_LEVELS.debug
    case 'test':
      return LOG_LEVELS.warn
    default:
      return LOG_LEVELS.silent
  }
}

const LOG_LEVEL: LogLevel = getLogLevel()

type LogFunctionName = 'debug' | 'log' | 'info' | 'warn' | 'error'

const LOG_FUNCTION_NAMES: { [L in LogFunctionName]: L } = {
  debug: 'debug',
  log: 'log',
  info: 'info',
  warn: 'warn',
  error: 'error',
} as const

const LOG_FUNCTION_NAME_TO_ENABLED_LOG_LEVELS_MAP: {
  [L in LogFunctionName]: Set<LogLevel>
} = {
  [LOG_FUNCTION_NAMES.debug]: new Set([LOG_LEVELS.debug]),
  [LOG_FUNCTION_NAMES.log]: new Set([LOG_LEVELS.debug, LOG_LEVELS.log]),
  [LOG_FUNCTION_NAMES.info]: new Set([
    LOG_LEVELS.debug,
    LOG_LEVELS.log,
    LOG_LEVELS.info,
  ]),
  [LOG_FUNCTION_NAMES.warn]: new Set([
    LOG_LEVELS.debug,
    LOG_LEVELS.log,
    LOG_LEVELS.info,
    LOG_LEVELS.warn,
  ]),
  [LOG_FUNCTION_NAMES.error]: new Set([
    LOG_LEVELS.debug,
    LOG_LEVELS.log,
    LOG_LEVELS.info,
    LOG_LEVELS.warn,
    LOG_LEVELS.error,
  ]),
}

const getIsLogFunctionEnabled = (logFunctionName: LogFunctionName): boolean => {
  const enabledLogLevels =
    LOG_FUNCTION_NAME_TO_ENABLED_LOG_LEVELS_MAP[logFunctionName]

  return enabledLogLevels.has(LOG_LEVEL)
}

/**
 * Returns a native console logging function wrapped in a thin wrapper which
 * suppresses logs or not depending on the current environment.
 *
 * @example getLogFunction('log')
 * // Returns a function which accepts the same arguments as console.log, and
 * // behaves the same as console.log except that it suppresses logs or not
 * // depending on the current environment.
 */
const getLogFunction = <L extends LogFunctionName>(
  logFunctionName: L,
): (typeof console)[L] => {
  const logFunction: (typeof console)[L] = (...args) => {
    if (getIsLogFunctionEnabled(logFunctionName)) {
      // eslint-disable-next-line no-restricted-syntax
      console[logFunctionName](...args)
    }
  }

  return logFunction
}

type Logger = { [L in LogFunctionName]: (typeof console)[L] } & {
  getIsLogFunctionEnabled: (logFunctionName: LogFunctionName) => boolean
}

/**
 * Custom logging utility which should be used throughout the application rather
 * than directly using native console logging functions such as console.log
 * (this is enforced via the no-restricted-syntax ESLint rule).
 *
 * Provides a thin wrapper around the native console logging functions,
 * suppressing logs or not depending on the current environment (for example
 * suppressing all logs in production).
 *
 * Also exposes getIsLogFunctionEnabled, which can be used to check if a
 * specific log function is enabled or not in the current environment.
 *
 * Intentionally does not expose implementation details such as the current
 * LOG_LEVEL, in order to ensure that log suppression logic is fully
 * consolidated and encapsulated within this utility so that any modifications
 * to the logic or or how it is implemented in this utility that we may make in
 * the future won't break consumers.
 */
export const logger: Logger = {
  debug: getLogFunction(LOG_FUNCTION_NAMES.debug),
  log: getLogFunction(LOG_FUNCTION_NAMES.log),
  info: getLogFunction(LOG_FUNCTION_NAMES.info),
  warn: getLogFunction(LOG_FUNCTION_NAMES.warn),
  error: getLogFunction(LOG_FUNCTION_NAMES.error),
  getIsLogFunctionEnabled,
}
